import React, { useState, useCallback, useRef, useEffect } from 'react';
import { styled, keyframes } from '@mui/material/styles';
import { Paper, List, Box, Popper } from '@mui/material';
import { Link } from 'react-router-dom';
import { MenuItem as MenuItemType } from '../../types';
import MenuContent from './MenuContent';

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const HorizontalListItem = styled('li')<{ isActive?: boolean; hasActiveChild?: boolean }>(({ isActive, hasActiveChild }) => ({
  display: 'inline-block',
  listStyle: 'none',
  '& > a, & > div': {
    padding: '8px 24px',
    display: 'flex',
    alignItems: 'center',
    color: isActive || hasActiveChild ? '#556ee6' : '#fff',
    textDecoration: 'none',
    cursor: 'pointer',
    minHeight: 40,
    fontSize: 15,
    fontWeight: 500,
    gap: 6,
    transition: 'color 0.15s ease',
    '&:hover': { color: '#556ee6' },
  },
}));

const DropdownPaper = styled(Paper)<{ isWideMenu?: boolean }>(({ isWideMenu }) => ({
  backgroundColor: '#2a3042',
  borderRadius: '0 0 8px 8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  border: 'none',
  borderTop: 'none',
  width: isWideMenu ? 800 : 'auto',
  minWidth: 180,
  padding: 12,
  animation: `${slideDown} 0.2s ease-out`,
  marginTop: 0,
}));

const SubMenuItem = styled('li')<{ isActive?: boolean }>(({ isActive }) => ({
  listStyle: 'none',
  '& > a, & > div': {
    padding: '6px 8px',
    display: 'flex',
    alignItems: 'center',
    color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.9)',
    textDecoration: 'none',
    cursor: 'pointer',
    backgroundColor: isActive ? '#556ee6' : 'transparent',
    minHeight: 36,
    minWidth: 180,
    borderRadius: 4,
    transition: 'all 0.2s ease',
    fontSize: 13,
    fontWeight: 500,
    margin: '2px 0',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.4,
    '&:hover': { color: '#fff', backgroundColor: '#556ee6' },
    '& .bx, & .fa': { marginRight: 8, fontSize: 14, minWidth: 16 },
  },
}));

const WideMenuContainer = styled(Box)<{ itemCount: number }>(({ itemCount }) => ({
  display: 'grid',
  gridTemplateColumns: itemCount <= 2 ? `repeat(${itemCount}, 1fr)` : 'repeat(2, 1fr)',
  gap: 20,
  padding: 16,
}));

const MenuSection = styled(Box)({
  minHeight: 150,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: 8,
  padding: 12,
  backgroundColor: 'rgba(255, 255, 255, 0.02)',
  '& .section-title': {
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.8)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    marginBottom: 12,
    textAlign: 'center',
    backgroundColor: 'rgba(85, 110, 230, 0.1)',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    '& i': { fontSize: 16, color: '#556ee6' },
  },
});

interface HorizontalMenuItemProps {
  item: MenuItemType;
  currentPath: string;
  onItemClick?: (item: MenuItemType) => void;
  level?: number;
}

const HOVER_DELAY = 20;

const HorizontalMenuItem = ({ 
  item, 
  currentPath, 
  onItemClick,
  level = 0 
}: HorizontalMenuItemProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const hasChildren = Boolean(item.subMenu?.length);
  const isActive = currentPath === item.path;
  
  const hasActiveChild = useCallback((items: MenuItemType[]): boolean => 
    items.some(subItem => subItem.path === currentPath || (subItem.subMenu?.length && hasActiveChild(subItem.subMenu))),
    [currentPath]
  );

  const hasActiveChildInside = hasChildren && hasActiveChild(item.subMenu!);
  const isWideMenu = hasChildren && item.subMenu!.some(subItem => subItem.subMenu?.length);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const closeDropdown = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setShowDropdown(false);
      setAnchorEl(null);
    }, HOVER_DELAY);
  }, []);

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (hasChildren && level === 0) {
      clearTimer();
      setAnchorEl(e.currentTarget);
      setShowDropdown(true);
    }
  };

  const handleClick = (clickedItem: MenuItemType) => {
    if (!clickedItem.subMenu?.length && clickedItem.path && onItemClick) {
      onItemClick(clickedItem);
      setShowDropdown(false);
      setAnchorEl(null);
    }
  };

  useEffect(() => clearTimer, [clearTimer]);

  const renderSubMenu = (subItems: MenuItemType[], subLevel = 1): React.ReactNode => {
    if (isWideMenu && subLevel === 1) {
      return (
        <WideMenuContainer itemCount={subItems.length}>
          {subItems.map((subItem, index) => {
            if (subItem.component) {
              const Component = subItem.component;
              return (
                <Box key={subItem.id || index} sx={{ p: 2 }}>
                  <Component {...(subItem.componentProps || {})} />
                </Box>
              );
            }
            
            return (
              <MenuSection key={subItem.id || index}>
                <Box className="section-title">
                  {subItem.icon && <i className={subItem.icon} />}
                  <span>{subItem.label}</span>
                </Box>
                <List sx={{ m: 0, p: 0 }}>
                  {subItem.subMenu?.map((childItem, childIdx) => {
                    if (childItem.component) {
                      const ChildComponent = childItem.component;
                      return (
                        <Box key={childItem.id || childIdx} sx={{ p: 1 }}>
                          <ChildComponent {...(childItem.componentProps || {})} />
                        </Box>
                      );
                    }
                    
                    return (
                      <SubMenuItem
                        key={childItem.id || childIdx}
                        isActive={currentPath === childItem.path}
                      >
                        {childItem.path ? (
                          <Link to={childItem.path} onClick={() => handleClick(childItem)}>
                            <MenuContent item={childItem} isCollapsed={false} level={subLevel + 1} isHorizontal />
                          </Link>
                        ) : (
                          <Box onClick={() => handleClick(childItem)}>
                            <MenuContent item={childItem} isCollapsed={false} level={subLevel + 1} isHorizontal />
                          </Box>
                        )}
                      </SubMenuItem>
                    );
                  })}
                </List>
              </MenuSection>
            );
          })}
        </WideMenuContainer>
      );
    }
    
    return (
      <List sx={{ m: 0, p: 0 }}>
        {subItems.map((subItem, index) => {
          if (subItem.component) {
            const Component = subItem.component;
            return (
              <Box key={subItem.id || index} sx={{ p: 2 }}>
                <Component {...(subItem.componentProps || {})} />
              </Box>
            );
          }
          
          return (
            <SubMenuItem
              key={subItem.id || index}
              isActive={currentPath === subItem.path}
            >
              {subItem.path ? (
                <Link to={subItem.path} onClick={() => handleClick(subItem)}>
                  <MenuContent item={subItem} isCollapsed={false} level={subLevel} isHorizontal />
                </Link>
              ) : (
                <Box onClick={() => handleClick(subItem)}>
                  <MenuContent item={subItem} isCollapsed={false} level={subLevel} isHorizontal />
                </Box>
              )}
            </SubMenuItem>
          );
        })}
      </List>
    );
  };

  return (
    <HorizontalListItem 
      isActive={isActive} 
      hasActiveChild={hasActiveChildInside}
      onMouseEnter={clearTimer}
      onMouseLeave={closeDropdown}
    >
      {!hasChildren && item.path ? (
        <Link to={item.path} onClick={() => handleClick(item)}>
          <MenuContent item={item} isCollapsed={false} level={level} isHorizontal />
        </Link>
      ) : (
        <div onMouseEnter={handleMouseEnter}>
          <MenuContent item={item} isCollapsed={false} level={level} isHorizontal />
        </div>
      )}

      {hasChildren && (
        <Popper 
          open={showDropdown} 
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: 1300 }}
          modifiers={[{ name: 'offset', options: { offset: isWideMenu ? [-350, -8] : [0, -8] } }]}
        >
          <DropdownPaper 
            elevation={8}
            isWideMenu={isWideMenu}
            onMouseEnter={clearTimer}
            onMouseLeave={closeDropdown}
          >
            {renderSubMenu(item.subMenu!)}
          </DropdownPaper>
        </Popper>
      )}
    </HorizontalListItem>
  );
};

export default HorizontalMenuItem;
